import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { useShallow } from 'zustand/react/shallow';
import { Vector3 } from 'three';
import { useSectionStore } from '../../store/sectionStore';
import { useClickStore } from '../../store/clickStore';
import { lookCameraPose } from '../../Utils/sectionMath';
import { getOrigin } from '../../config/siteConfig';

export default function SectionLookRig({ controlRef }) {
  const camera = useThree((s) => s.camera);
  const invalidate = useThree((s) => s.invalidate);
  const {
    enabled,
    lookLocked,
    lookFlip,
    orientation,
    centerEast,
    centerNorth,
    centerRl,
    azimuth,
    fencePoints,
    fenceDistance,
    obliqueNormal,
  } = useSectionStore(
    useShallow((s) => ({
      enabled: s.enabled,
      lookLocked: s.lookLocked,
      lookFlip: s.lookFlip,
      orientation: s.orientation,
      centerEast: s.centerEast,
      centerNorth: s.centerNorth,
      centerRl: s.centerRl,
      azimuth: s.azimuth,
      fencePoints: s.fencePoints,
      fenceDistance: s.fenceDistance,
      obliqueNormal: s.obliqueNormal,
    }))
  );

  useEffect(() => {
    if (lookLocked) useClickStore.getState().setFlyAround(false);
  }, [lookLocked]);

  useEffect(() => {
    const controls = controlRef?.current;
    if (!controls) return undefined;
    const prevRotate = controls.enableRotate;
    if (enabled && lookLocked) {
      controls.enableRotate = false;
    }
    return () => {
      controls.enableRotate = prevRotate;
      camera.up.set(0, 1, 0);
    };
  }, [enabled, lookLocked, camera, controlRef]);

  useEffect(() => {
    const controls = controlRef?.current;
    if (!enabled || !lookLocked || !controls || !camera) return;
    const pose = lookCameraPose(
      {
        enabled,
        lookLocked,
        lookFlip,
        orientation,
        centerEast,
        centerNorth,
        centerRl,
        azimuth,
        fencePoints,
        fenceDistance,
        obliqueNormal,
      },
      camera,
      controls,
      getOrigin()
    );
    camera.up.copy(pose.up || new Vector3(0, 1, 0));
    camera.position.copy(pose.position);
    controls.target.copy(pose.target);
    camera.lookAt(pose.target);
    controls.update();
    invalidate();
  }, [
    enabled,
    lookLocked,
    lookFlip,
    orientation,
    centerEast,
    centerNorth,
    centerRl,
    azimuth,
    fencePoints,
    fenceDistance,
    obliqueNormal,
    camera,
    controlRef,
    invalidate,
  ]);

  return null;
}
